import React, { useState, useEffect, useRef, useMemo } from 'react';
import { storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, updateDoc, query, getDocs, where, doc, writeBatch, serverTimestamp } from 'firebase/firestore';
import Modal from './Modal';
import * as faceapi from '@vladmandic/face-api';
import RelationshipField from './RelationshipField';
import { auth } from '../firebase';

const AddCardForm = ({ onClose, userId, existingGroups = [], cards = [] }) => {
  // Form Fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [info, setInfo] = useState('');
  const [spouse, setSpouse] = useState('');
  const [children, setChildren] = useState('');
  const [parents, setParents] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [linkedIn, setLinkedIn] = useState('');
  const [pronunciation, setPronunciation] = useState('');
  const [currentGroup, setCurrentGroup] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  
  // Image handling
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState('');

  // Group suggestions
  const [showGroupSuggestions, setShowGroupSuggestions] = useState(false);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const groupInputRef = useRef(null);

  // Photo album handling
  const [showPhotoAlbum, setShowPhotoAlbum] = useState(false);
  const [albumPhotos, setAlbumPhotos] = useState([]);

  // Relationships
  const [relationships, setRelationships] = useState({
    spouse: null,
    parents: [],
    children: [],
    siblings: []
  });

  // Model loading
  const [modelLoaded, setModelLoaded] = useState(false);

  // Defensive: ensure existingGroups are objects
  const safeExistingGroups = useMemo(() => 
    (existingGroups || []).map(group =>
      typeof group === 'string' ? { id: group, name: group } : group
    ),
    [existingGroups]
  );

  // Handle click outside group suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (groupInputRef.current && !groupInputRef.current.contains(event.target)) {
        setShowGroupSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter groups based on input
  useEffect(() => {
    const filtered = safeExistingGroups.filter(g => 
      g.name.toLowerCase().includes(currentGroup.toLowerCase()) &&
      !selectedGroups.includes(g.name)
    );
    setFilteredGroups(filtered);
  }, [currentGroup, safeExistingGroups, selectedGroups]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Add function to load photos
  const loadAlbumPhotos = async () => {
    try {
      const q = query(collection(db, 'photoAlbum'), where('userId', '==', userId));
      const snapshot = await getDocs(q);
      const photos = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAlbumPhotos(photos);
      console.log('Loaded album photos:', photos);
    } catch (error) {
      console.error('Error loading album photos:', error);
    }
  };

  // Add this useEffect to load photos when showing the album
  useEffect(() => {
    if (showPhotoAlbum) {
      loadAlbumPhotos();
    }
  }, [showPhotoAlbum]);

  // Add model loading effect
  useEffect(() => {
    const loadModels = async () => {
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri('/models'),
          faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
          faceapi.nets.faceRecognitionNet.loadFromUri('/models')
        ]);
        setModelLoaded(true);
        console.log('Face-api models loaded successfully');
      } catch (error) {
        console.error('Error loading face-api models:', error);
      }
    };
    loadModels();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGroupSelect = (selectedGroup) => {
    if (!selectedGroups.includes(selectedGroup)) {
      setSelectedGroups([...selectedGroups, selectedGroup]);
    }
    setCurrentGroup('');
    setShowGroupSuggestions(false);
  };

  const handleRemoveGroup = (groupToRemove) => {
    setSelectedGroups(selectedGroups.filter(g => g !== groupToRemove));
  };

  const handleGroupKeyDown = (e) => {
    if (e.key === 'Enter' && currentGroup.trim()) {
      e.preventDefault();
      const newGroup = currentGroup.trim();
      if (!selectedGroups.includes(newGroup)) {
        setSelectedGroups([...selectedGroups, newGroup]);
      }
      setCurrentGroup('');
      setShowGroupSuggestions(false);
    }
  };

  const validateLinkedInUrl = (url) => {
    if (!url) return true; // Allow empty URL
    return url.startsWith('https://www.linkedin.com/') || 
           url.startsWith('http://www.linkedin.com/') ||
           url.startsWith('www.linkedin.com/') ||
           url.startsWith('linkedin.com/');
  };

  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 400; // Reduced from 800
          const MAX_HEIGHT = 400; // Reduced from 800
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with reduced quality
          canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/jpeg', 0.5); // Reduced quality to 50%
        };
      };
    });
  };

  const extractFaceDescriptor = async (imageUrl) => {
    if (!modelLoaded) {
      console.log('Face-api models not yet loaded');
      return null;
    }

    try {
      console.log('Starting face detection for:', imageUrl);
      const img = await faceapi.fetchImage(imageUrl);
      
      const detection = await faceapi
        .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();
      
      if (!detection) {
        console.log('No face detected in the image');
        return null;
      }
      
      console.log('Face detection successful');
      return Array.from(detection.descriptor);
    } catch (error) {
      console.error('Face detection error:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    try {
      setLoading(true);
      let imageUrl = null;
      let faceDescriptor = null;

      if (image) {
        console.log('Step 1: Preparing to upload image:', image);
        console.log('Step 1.5: Checking auth state:', auth.currentUser?.uid);
        console.log('Step 1.6: Storage bucket config:', storage.app.options.storageBucket);
        
        const imageRef = ref(storage, `cards/${userId}/${Date.now()}-${image.name}`);
        console.log('Step 2: Uploading to:', imageRef.fullPath);
        console.log('Step 2.5: Storage reference created, attempting upload...');
        
        try {
          await uploadBytes(imageRef, image);
          console.log('Step 3: Image uploaded! Getting download URL...');
        } catch (uploadError) {
          console.error('Step 2.5: Upload failed with error:', uploadError);
          console.error('Upload error details:', {
            code: uploadError.code,
            message: uploadError.message,
            serverResponse: uploadError.serverResponse
          });
          throw uploadError;
        }
        
        imageUrl = await getDownloadURL(imageRef);
        console.log('Step 4: Download URL obtained:', imageUrl);

        // Extract face descriptor after we have the image URL
        if (imageUrl) {
          try {
            console.log('Step 5: Extracting face descriptor from:', imageUrl);
            faceDescriptor = await extractFaceDescriptor(imageUrl);
            console.log('Step 6: Face descriptor extracted:', faceDescriptor ? 'Success' : 'Not found', faceDescriptor);
          } catch (err) {
            console.error('Step 6: Face descriptor extraction failed:', err);
            faceDescriptor = null;
          }
        }
      } else {
        console.log('No image provided, skipping upload and face descriptor extraction.');
      }

      const cardData = {
        firstName,
        lastName,
        email,
        phone,
        info,
        relationships,
        company,
        role,
        linkedIn,
        pronunciation,
        groups: selectedGroups,
        imageUrl,
        userId,
        createdAt: new Date().toISOString(),
        deleted: false
      };

      // Only add faceDescriptor if it exists
      if (faceDescriptor) {
        cardData.faceDescriptor = faceDescriptor;
        cardData.faceApiDescriptor = faceDescriptor;
      }

      console.log('Step 7: Preparing Firestore batch write with cardData:', cardData);
      // Create batch for atomic updates
      const batch = writeBatch(db);

      // Add the new card
      const cardsRef = collection(db, 'cards');
      const newCardRef = doc(cardsRef);
      batch.set(newCardRef, cardData);

      // Handle bidirectional relationships
      if (relationships.spouse) {
        const spouseRef = doc(db, 'cards', relationships.spouse.cardId);
        batch.update(spouseRef, {
          'relationships.spouse': {
            cardId: newCardRef.id,
            name: `${firstName} ${lastName}`,
            group: selectedGroups[0] || ''
          }
        });
      }

      console.log('Step 8: Committing Firestore batch...');
      // Commit all updates
      await batch.commit();
      console.log('Step 9: Firestore batch committed successfully. Closing form.');
      onClose();
    } catch (error) {
      console.error('Error creating card:', error);
      alert('Failed to create card. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Rest of the component remains the same until the groups section
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] overflow-y-auto">
      <div className="min-h-screen px-4 pt-20 pb-10 flex flex-col items-center">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-xl">
            {/* Form Header */}
            <div className="sticky top-0 bg-white px-6 py-4 border-b rounded-t-lg">
              <h2 className="text-2xl font-bold text-gray-900">Add New Card</h2>
            </div>

            {/* Scrollable Form Content */}
            <div className="px-6 py-4">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Image Section */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700">Photo</label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setShowPhotoAlbum(true)}
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Choose from Photo Gallery
                    </button>
                    <label
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 cursor-pointer text-center"
                    >
                      Choose File
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {/* Add image preview */}
                  {(preview || (image && image.type === 'albumPhoto')) && (
                    <div className="mt-4">
                      <div className="relative w-32 h-32 mx-auto">
                        <img
                          src={preview || image.url}
                          alt="Preview"
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImage(null);
                            setPreview(null);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Name Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="w-full p-2 border rounded"
                    placeholder="Enter person's first name"
                  />
                </div>

                {/* Last Name Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="Enter person's last name"
                  />
                </div>

                {/* Family Section */}
                <div className="border-t pt-4">
                  <h3 className="font-medium text-gray-900 mb-3">Family Information</h3>
                  <div className="space-y-4">
                    <RelationshipField
                      label="Spouse"
                      value={relationships.spouse}
                      onChange={(relation) => setRelationships({
                        ...relationships,
                        spouse: relation
                      })}
                      cards={cards}
                      currentCardId={null}
                      placeholder="Search for spouse..."
                    />

                    <RelationshipField
                      label="Parents"
                      value={relationships.parents}
                      onChange={(relations) => setRelationships({
                        ...relationships,
                        parents: relations
                      })}
                      cards={cards}
                      currentCardId={null}
                      placeholder="Search for parents..."
                      multiple={true}
                    />

                    <RelationshipField
                      label="Children"
                      value={relationships.children}
                      onChange={(relations) => setRelationships({
                        ...relationships,
                        children: relations
                      })}
                      cards={cards}
                      currentCardId={null}
                      placeholder="Search for children..."
                      multiple={true}
                    />
                  </div>
                </div>

                {/* Professional Section */}
                <div className="border-t pt-4">
                  <h3 className="font-medium text-gray-900 mb-3">Professional Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company
                      </label>
                      <input
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="Enter company name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <input
                        type="text"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="Enter role/title"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        LinkedIn Profile
                      </label>
                      <input
                        type="text"
                        value={linkedIn}
                        onChange={(e) => setLinkedIn(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="Enter LinkedIn URL"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="Enter email address"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="Enter phone number"
                      />
                    </div>
                  </div>
                </div>

                {/* Groups Section with Tags */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Groups *
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Choose from an existing Group, or add a new Group by typing its name and pressing enter
                  </p>
                  <div className="space-y-2">
                    {/* Selected Groups Tags */}
                    {selectedGroups.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {selectedGroups.map(group => {
                          const groupObj = typeof group === 'string' ? { id: group, name: group } : group;
                          return (
                            <span
                              key={groupObj.id || groupObj.name}
                              className="inline-flex items-center px-2 py-1 rounded-full text-sm bg-gray-100"
                            >
                              {groupObj.name}
                              <button
                                type="button"
                                onClick={() => handleRemoveGroup(groupObj.id || groupObj.name)}
                                className="ml-1 text-gray-500 hover:text-gray-700"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Group Input with Autocomplete */}
                    <div ref={groupInputRef} className="relative">
                      <input
                        type="text"
                        value={currentGroup}
                        onChange={(e) => {
                          setCurrentGroup(e.target.value);
                          setShowGroupSuggestions(true);
                        }}
                        onKeyDown={handleGroupKeyDown}
                        onFocus={() => setShowGroupSuggestions(true)}
                        className="w-full p-2 border rounded"
                        placeholder="Group Name"
                      />
                      {showGroupSuggestions && filteredGroups.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                          {filteredGroups.map((suggestion) => {
                            const suggestionObj = typeof suggestion === 'string' ? { id: suggestion, name: suggestion } : suggestion;
                            return (
                              <div
                                key={suggestionObj.id || suggestionObj.name}
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                onClick={() => handleGroupSelect(suggestionObj.name)}
                              >
                                {suggestionObj.name}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="border-t pt-4">
                  <h3 className="font-medium text-gray-900 mb-3">Notes</h3>
                  <div>
                    <textarea
                      value={info}
                      onChange={(e) => setInfo(e.target.value)}
                      className="w-full p-2 border rounded text-left"
                      placeholder="Enter any additional notes about this person"
                      rows="3"
                    />
                  </div>
                </div>

                {/* Pronunciation Section */}
                <div className="border-t pt-4">
                  <h3 className="font-medium text-gray-900 mb-3">Pronunciation</h3>
                  <div>
                    <input
                      type="text"
                      value={pronunciation}
                      onChange={(e) => setPronunciation(e.target.value)}
                      className="w-full p-2 border rounded"
                      placeholder="How to pronounce their name (e.g., 'jah-nay')"
                    />
                  </div>
                </div>
              </form>
            </div>

            {/* Form Footer */}
            <div className="sticky bottom-0 bg-white px-6 py-4 border-t rounded-b-lg">
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-4 py-2 bg-[#04b53a] text-white rounded hover:bg-[#039431] disabled:opacity-50"
                >
                  {loading ? 'Adding...' : 'Add Card'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Photo Album Modal */}
      {showPhotoAlbum && (
        <Modal onClose={() => setShowPhotoAlbum(false)}>
          <div className="grid grid-cols-3 gap-4">
            {albumPhotos.map(photo => (
              <div 
                key={photo.id} 
                className="cursor-pointer"
                onClick={() => {
                  setImage({
                    type: 'albumPhoto',
                    photoId: photo.id,
                    url: photo.imageUrl
                  });
                  setShowPhotoAlbum(false);
                }}
              >
                <img 
                  src={photo.imageUrl} 
                  alt="" 
                  className="w-full h-32 object-cover rounded"
                />
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AddCardForm;